import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

import { Headline, Loading, Refusal, Tooltip } from "@lopie/kernel-ui";

import { clearHashParams, parseHash } from "../hash.js";
import { Label, PreviewDialog, PrincipalCard } from "./Access.jsx";
import { Seed, UserDrawer, sourceOf } from "./Users.jsx";
import { figureFor, isNotForThem, validAddress, validPerDay, whose } from "./askLimits.js";

/**
 * People: what each one can see, and what each one is told.
 *
 * ## Why these were two screens, and why they are one
 *
 * Access answered *who can see what* and Users answered *who gets told what*.
 * Both rendered a list of people, both had their own way of adding somebody,
 * and anybody who could sign in *and* wanted telling appeared on both with
 * nothing saying they were the same person. That is what made them read as
 * duplicates.
 *
 * They were not duplicates. They are two facts about one person, and the
 * screens had been split along the boundary of the *system that stores each
 * fact* rather than the boundary of the thing a reader is looking at.
 *
 * ## What must not merge, and it is the reason the data stays apart
 *
 * The populations differ, and deliberately:
 *
 * - **Access reads through to Cognito.** It is the set of people who can sign
 *   in, and their groups.
 * - **The roster is the kernel's own.** It holds people with *no login at
 *   all* — the finance lead who should hear that an agent blew its budget does
 *   not appear in the sign-in pool, and creating an account so the kernel can
 *   email them is inventing a credential to hold an address.
 *
 * So the two are joined for display and never folded into one store. The
 * kernel's own note on `users.py` gives the sharp version: a group is a tag,
 * not a subscription, and *"overloading it into one would mean granting
 * somebody access to records in order to email them."* On this screen those
 * remain two separate acts with two separate confirmations, which is what
 * stops subscribing somebody to an alert from widening what they can read.
 *
 * ## The join is the email address
 *
 * Both sides already key on it — Cognito's username is the address here, and
 * `users.py` keys the roster on it explicitly because *"the email is the thing
 * a notification is sent to"*. So a row is an address, and it carries whichever
 * of the two halves exist for it. A person can hold either, both, or one.
 *
 * ## Each half keeps its own argument
 *
 * Clicking a group chip still runs the preview first — *"this makes 3 more
 * Organizations visible to them"* — before anything changes, because finding
 * out afterwards is not something you can undo for a person who has already
 * seen a record. Clicking the notifications line still opens the roster
 * editor. Neither flow was rebuilt; they were moved onto one row.
 */

/** `p.email` or the username, lowercased. Absent is its own answer. */
function keyOf(value) {
  return String(value || "").trim().toLowerCase();
}

/**
 * What one person is told, which is the half the sign-in pool cannot answer.
 *
 * A button rather than a row of chips with an edit control beside it: the
 * whole line is the affordance, the same way the roster screen made each
 * person clickable. There is nothing here to read past.
 */
function ToldAbout({ user, canWrite, onOpen }) {
  const topics = user?.topics || [];
  return (
    <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: "divider" }}>
      <Label>Told about</Label>
      <Box
        component="button"
        type="button"
        disabled={!user || !canWrite}
        onClick={() => user && onOpen(user)}
        sx={{
          display: "block",
          width: "100%",
          textAlign: "left",
          background: "none",
          border: 0,
          p: 0,
          mt: 0.5,
          cursor: user && canWrite ? "pointer" : "default",
          font: "inherit",
          color: "inherit",
        }}
      >
        {!user ? (
          <Typography variant="body2" color="text.secondary">
            Not on the notification roster, so nothing this kernel notices
            reaches them. Being able to sign in does not put somebody on it —
            they are separate lists, and this one is added to below.
          </Typography>
        ) : topics.length ? (
          <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap" }}>
            {topics.map((t) => (
              <Chip key={t} size="small" label={t} />
            ))}
            <Chip size="small" variant="outlined" label={sourceOf(user)} />
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            On the roster and subscribed to nothing, so they hear nothing.
            Added {sourceOf(user)}.
          </Typography>
        )}
      </Box>
    </Box>
  );
}

/**
 * One person, both halves.
 *
 * The access half is `PrincipalCard` itself rather than a second rendering of
 * the same thing. That card already draws the groups this app cannot change,
 * the "grants nothing" note, the governor and disabled chips and the sentence
 * about holding no groups — a reimplementation here started by losing all
 * five, which is the argument for not having two.
 *
 * Somebody with no login gets the plainer card below. They cannot hold a
 * group, so there is no access half to draw, and drawing an empty one would
 * invite somebody to fix it by creating an account.
 */
const dollars = (n) => `$${Number(n).toLocaleString(undefined, {
  minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** One of the three Builder limits: what it is, whose decision it is, and the
 *  one thing you can do to it. The source is said in words next to the figure,
 *  because "$50" alone does not say whether a governor chose it. */
function LimitRow({ label, hint, figure, set, by, bounds, busy, onSave }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const n = Number(value);
  const valid = value !== "" && Number.isFinite(n) && n >= bounds.min_usd && n <= bounds.max_usd;
  return (
    <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", flexWrap: "wrap" }}>
      <Box sx={{ minWidth: "15rem", flex: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
        <Typography variant="caption" color="text.secondary">{hint}</Typography>
      </Box>
      {editing ? (
        <>
          <TextField
            size="small" type="number" label="US dollars" value={value} autoFocus
            onChange={(e) => setValue(e.target.value)}
            slotProps={{ htmlInput: { min: bounds.min_usd, max: bounds.max_usd, step: 1,
                                      "aria-label": `${label}, in dollars` } }}
            sx={{ width: "9rem" }}
          />
          <Button size="small" variant="contained" disabled={busy || !valid}
            onClick={async () => { if (await onSave(n)) setEditing(false); }}>
            Save
          </Button>
          <Button size="small" onClick={() => setEditing(false)}>Cancel</Button>
        </>
      ) : (
        <>
          <Typography variant="h6" sx={{ fontVariantNumeric: "tabular-nums" }}>{dollars(figure)}</Typography>
          <Chip size="small" variant="outlined"
            label={set ? `set by ${by || "a governor"}` : "built in: nobody has set one"} />
          <Button size="small" disabled={busy} onClick={() => { setValue(String(figure)); setEditing(true); }}>
            Change
          </Button>
        </>
      )}
    </Stack>
  );
}

/** What a person may spend through Builder, and what one chat may cost.
 *
 *  Set here and nowhere else: Builder reads it from the kernel. The sentence
 *  about what it does not stop is attached to the figures it qualifies. */
function BuilderLimits({ limits, failure, problem, busy, onSave, onDismiss }) {
  if (failure) {
    const code = failure.body?.error;
    return (
      <Alert severity="info" variant="outlined">
        <AlertTitle>
          {code === "not_a_governor" ? "Spend limits are for governors"
            : code === "not_in_manifest" ? "This app may not show spend limits"
            : "Spend limits could not be read"}
        </AlertTitle>
        <Typography variant="body2">
          {code === "not_in_manifest"
            ? <>This app does not declare <code>budget.set</code>, so it can neither show nor change what people may spend through Builder.</>
            : failure.body?.detail || failure.message}
        </Typography>
      </Alert>
    );
  }
  if (!limits) return null;
  return (
    <Paper variant="outlined" sx={{ p: 2, minWidth: 0 }}>
      <Stack spacing={1.5}>
        <Label>Builder spend limits</Label>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: "62ch" }}>
          What the chats a person starts through Builder may cost, in the
          organization's Anthropic account. Somebody with a limit of their own
          is held to it instead of the one for everybody; remove it on their
          card below.
        </Typography>
        {problem ? (
          <Alert severity="error" variant="outlined" onClose={onDismiss}>{problem}</Alert>
        ) : null}
        <LimitRow
          label="Each person, each month"
          hint="The most one person may start in a calendar month."
          figure={limits.default?.limit_usd ?? limits.built_in.person_usd}
          set={Boolean(limits.default)} by={limits.default?.set_by}
          bounds={limits.bounds} busy={busy}
          onSave={(usd) => onSave({ scope: "default", limit_usd: usd })}
        />
        <LimitRow
          label="Each chat"
          hint="The most one chat may cost. Fixed when the chat starts."
          figure={limits.chat?.limit_usd ?? limits.built_in.chat_usd}
          set={Boolean(limits.chat)} by={limits.chat?.set_by}
          bounds={limits.bounds} busy={busy}
          onSave={(usd) => onSave({ scope: "chat", limit_usd: usd })}
        />
        {limits.people.length ? (
          <Typography variant="body2" color="text.secondary">
            Their own limit: {limits.people.map((p) => `${p.username} ${dollars(p.limit_usd)}`).join(" · ")}
          </Typography>
        ) : null}
        <Typography variant="caption" color="text.secondary" sx={{ maxWidth: "70ch" }}>
          {(limits.does_not_prove || [])[0]}
        </Typography>
      </Stack>
    </Paper>
  );
}

/** How many questions a person may put to the model in Insights a day (LO-222).
 *
 *  Kernel configuration like the limits above, set by a governor and read back from the kernel. Shown only to
 *  a person it is for: where the kernel says it is not (not a governor, an app that does not declare the
 *  action) the panel is absent and nothing else on the screen changes. */
function InsightsAskLimits({ limits, problem, busy, onSave, onDismiss }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const [who, setWho] = useState("");
  const [own, setOwn] = useState("");
  if (!limits) return null;
  const bounds = limits.bounds;
  const everybody = figureFor({ ...limits, people: [] }, "");
  return (
    <Paper variant="outlined" sx={{ p: 2, minWidth: 0 }}>
      <Stack spacing={1.5}>
        <Label>Insights questions</Label>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: "62ch" }}>
          How many questions in words a person may put to the model in Insights each day (UTC). It is counted
          before the model is called, so a question over the limit costs nothing and says when the day turns over.
        </Typography>
        {problem ? <Alert severity="error" variant="outlined" onClose={onDismiss}>{problem}</Alert> : null}
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", flexWrap: "wrap" }}>
          <Box sx={{ minWidth: "15rem", flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Each person, each day</Typography>
            <Typography variant="caption" color="text.secondary">Everybody who has no limit of their own.</Typography>
          </Box>
          {editing ? (
            <>
              <TextField
                size="small" type="number" label="Questions a day" value={value} autoFocus
                onChange={(e) => setValue(e.target.value)}
                slotProps={{ htmlInput: { min: bounds.min, max: bounds.max, step: 1, "aria-label": "Questions each person may ask each day" } }}
                sx={{ width: "9rem" }}
              />
              <Button size="small" variant="contained" disabled={busy || !validPerDay(value, bounds)}
                onClick={async () => { if (await onSave({ scope: "default", per_day: Number(value) })) setEditing(false); }}>
                Save
              </Button>
              <Button size="small" onClick={() => setEditing(false)}>Cancel</Button>
            </>
          ) : (
            <>
              <Typography variant="h6" sx={{ fontVariantNumeric: "tabular-nums" }}>{everybody.per_day}</Typography>
              <Chip size="small" variant="outlined"
                label={limits.default ? `set by ${limits.default.set_by || "a governor"}` : "built in: nobody has set one"} />
              <Button size="small" disabled={busy} onClick={() => { setValue(String(everybody.per_day)); setEditing(true); }}>
                Change
              </Button>
            </>
          )}
        </Stack>
        {(limits.people || []).map((p) => (
          <Stack key={p.username} direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
            <Typography variant="body2">{p.username}: {p.per_day} a day · {whose(figureFor(limits, p.username))}</Typography>
            <Button size="small" disabled={busy}
              onClick={() => onSave({ scope: "person", username: p.username, per_day: null })}>
              Use the default
            </Button>
          </Stack>
        ))}
        <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
          <TextField size="small" label="Give one person their own" value={who} placeholder="name@lopie.dev"
            onChange={(e) => setWho(e.target.value)} sx={{ width: "15rem" }} />
          <TextField size="small" type="number" label="Questions a day" value={own}
            onChange={(e) => setOwn(e.target.value)}
            slotProps={{ htmlInput: { min: bounds.min, max: bounds.max, step: 1, "aria-label": "Their own questions a day" } }}
            sx={{ width: "9rem" }} />
          <Button size="small" variant="outlined"
            disabled={busy || !validAddress(who) || !validPerDay(own, bounds)}
            onClick={async () => {
              if (await onSave({ scope: "person", username: who.trim().toLowerCase(), per_day: Number(own) })) { setWho(""); setOwn(""); }
            }}>
            Set
          </Button>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ maxWidth: "70ch" }}>
          {(limits.does_not_prove || [])[0]}
        </Typography>
      </Stack>
    </Paper>
  );
}

/** One person's Builder limit, on their card: which figure applies to them and
 *  whose decision it is, a way to give them their own, and a way back to the
 *  limit for everybody. */
function PersonLimit({ username, limits, busy, onSave }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const own = limits.people.find((p) => p.username === String(username).toLowerCase());
  const figure = own ? own.limit_usd : (limits.default?.limit_usd ?? limits.built_in.person_usd);
  const where = own ? `their own, set by ${own.set_by}`
    : limits.default ? `the limit for everybody, set by ${limits.default.set_by}`
    : "the built-in limit: nobody has set one";
  const n = Number(value);
  const valid = value !== "" && Number.isFinite(n) && n >= limits.bounds.min_usd && n <= limits.bounds.max_usd;
  return (
    <Box sx={{ mt: 1.5 }}>
      <Label>Builder spend</Label>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap", mt: 0.5 }}>
        {editing ? (
          <>
            <TextField
              size="small" type="number" label="US dollars a month" value={value} autoFocus
              onChange={(e) => setValue(e.target.value)}
              slotProps={{ htmlInput: { min: limits.bounds.min_usd, max: limits.bounds.max_usd, step: 1,
                                        "aria-label": `${username}'s own Builder limit, in dollars a month` } }}
              sx={{ width: "11rem" }}
            />
            <Button size="small" variant="contained" disabled={busy || !valid}
              onClick={async () => {
                if (await onSave({ scope: "person", username, limit_usd: n })) setEditing(false);
              }}>
              Save
            </Button>
            <Button size="small" onClick={() => setEditing(false)}>Cancel</Button>
          </>
        ) : (
          <>
            <Typography variant="body2">{dollars(figure)} a month · {where}</Typography>
            <Button size="small" disabled={busy}
              onClick={() => { setValue(String(figure)); setEditing(true); }}>
              {own ? "Change" : "Set their own"}
            </Button>
            {own ? (
              <Button size="small" disabled={busy}
                onClick={() => onSave({ scope: "person", username, limit_usd: null })}>
                Use the default
              </Button>
            ) : null}
          </>
        )}
      </Stack>
    </Box>
  );
}

/**
 * Which kernel-shipped apps this person may open (Builder).
 *
 * Not a group chip beside the tags, and not through the preview: it is not a
 * tag, it widens no read, so there is nothing for a preview to count. The
 * sentence under it says that, on the control it qualifies.
 */
function AppAccess({ principal, apps, mayGrant, busy, onToggle, limits, limitBusy, onLimit }) {
  if (!apps.length) return null;
  const held = new Set(principal.app_access || []);
  return (
    <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: "divider" }}>
      <Label>Can open</Label>
      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", mt: 0.5, alignItems: "center" }}>
        {apps.map((app) => (
          <FormControlLabel
            key={app.name}
            control={
              <Switch
                size="small"
                checked={held.has(app.name)}
                disabled={!mayGrant || busy}
                onChange={(e) => onToggle(principal.username, app.name, e.target.checked)}
              />
            }
            label={app.name === "app-builder" ? "Builder" : app.name}
          />
        ))}
      </Stack>
      <Typography variant="body2" color="text.secondary">
        App access, not a tag: it opens the app and changes nothing about what
        they can see. What Builder makes still needs a governor's approval.
      </Typography>
      {held.has("app-builder") && limits ? (
        <PersonLimit username={principal.username} limits={limits}
                     busy={busy || limitBusy} onSave={onLimit} />
      ) : null}
    </Box>
  );
}

function PersonCard({ person, groups, grantable, mayGrant, canWriteRoster,
                      onGroup, onTopics, busy, apps, onApp, limits, limitBusy, onLimit }) {
  const { email, principal, user } = person;
  const told = (
    <>
      {principal ? (
        <AppAccess principal={principal} apps={apps} mayGrant={mayGrant}
                   busy={busy} onToggle={onApp} limits={limits}
                   limitBusy={limitBusy} onLimit={onLimit} />
      ) : null}
      <ToldAbout user={user} canWrite={canWriteRoster} onOpen={onTopics} />
    </>
  );

  if (principal) {
    return (
      <PrincipalCard
        principal={principal}
        groups={groups}
        grantable={grantable}
        mayGrant={mayGrant}
        busy={busy}
        onChange={onGroup}
        footer={told}
      />
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, minWidth: 0 }}>
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: "center", flexWrap: "wrap", mb: 1 }}
      >
        <Typography
          variant="h2"
          sx={{ fontFamily: "monospace", overflowWrap: "anywhere", minWidth: 0 }}
        >
          {email}
        </Typography>
        {user?.name ? (
          <Typography variant="body2" color="text.secondary">
            {user.name}
          </Typography>
        ) : null}
        <Tooltip title="Not in the sign-in pool. They receive notifications and can open nothing, which is the ordinary case for an address that only needs telling — and is why this roster is the kernel's own rather than a set of Cognito groups.">
          <Chip size="small" variant="outlined" label="no login" />
        </Tooltip>
      </Stack>
      <Typography variant="body2" color="text.secondary">
        Cannot sign in, so there is no record they could open and no group here
        would change that.
      </Typography>
      {told}
    </Paper>
  );
}

export default function People({ get, post, remove, onEffective }) {
  // Two sources, held apart. Merging them into one state object would be the
  // first step toward merging the stores, and the stores must not merge.
  const [access, setAccess] = useState(null);
  const [roster, setRoster] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [failure, setFailure] = useState(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);

  // Builder's spend limits: kernel configuration, held apart from both halves.
  const [limits, setLimits] = useState(null);
  const [limitsFailure, setLimitsFailure] = useState(null);
  const [limitProblem, setLimitProblem] = useState(null);
  const [limitBusy, setLimitBusy] = useState(false);
  // How many questions a person may ask Insights a day: a second kernel setting of the same kind.
  const [askLimits, setAskLimits] = useState(null);
  const [askProblem, setAskProblem] = useState(null);
  const [askBusy, setAskBusy] = useState(false);
  const [askFailure, setAskFailure] = useState(null);

  // The access half.
  const [pending, setPending] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewFailure, setPreviewFailure] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [granting, setGranting] = useState(false);
  const [outcome, setOutcome] = useState(null);

  // A mark on an app's access list links here with the person and the tag
  // they lack (`#people?user=...&tag=...`), read once. It opens the same
  // grant, with the same preview and the same confirmation, as clicking the
  // chip; it never grants anything by arriving.
  const [link] = useState(() => {
    const params = parseHash(window.location.hash).params;
    const user = params.get("user");
    const tag = params.get("tag");
    return user && tag ? { user, tag } : null;
  });
  const linkRead = useRef(false);

  // The roster half.
  const [openEmail, setOpenEmail] = useState(null);
  const [draft, setDraft] = useState({ email: "", name: "" });

  const load = useCallback(async () => {
    try {
      const body = await get("/users");
      setRoster(body);
      setFailure(null);
    } catch (e) {
      setFailure(e);
      setRoster(null);
      return;
    }
    // Best effort, both of them, and for the reason the roster screen already
    // gave: a console that refused to show people because a directory was
    // unreachable would be failing at the thing somebody came for.
    get("/access").then(setAccess).catch(() => setAccess(null));
    get("/budgets/builder")
      .then((body) => { setLimits(body); setLimitsFailure(null); })
      .catch((e) => { setLimits(null); setLimitsFailure(e); });
    get("/insights/ask/limit")
      .then((body) => { setAskLimits(body); setAskFailure(null); })
      .catch((e) => { setAskLimits(null); setAskFailure(isNotForThem(e) ? null : e); });
    get("/people", { limit: 100 })
      .then((p) => setCandidates(p.items || []))
      .catch(() => setCandidates([]));
  }, [get]);

  useEffect(() => {
    load();
    onEffective?.(null);
  }, [load, onEffective]);

  // Once the people have loaded, say what the link asked for: the grant ready
  // to confirm, or the reason it cannot be offered. A refusal is information —
  // a link that silently opened nothing would read as a link that was broken.
  useEffect(() => {
    if (!link || !access || linkRead.current) return;
    linkRead.current = true;
    clearHashParams();
    const wanted = keyOf(link.user);
    const person = (access.principals || []).find(
      (p) => keyOf(p.username) === wanted || keyOf(p.email) === wanted,
    );
    const stop = (why) => setOutcome({
      ok: false,
      error: { message: `You followed a link to give ${link.user} the ${link.tag} tag, and it cannot be done from here: ${why}.` },
    });
    if (!access.can_grant) return stop("this app does not declare access.grant");
    if (!access.governance?.may_govern) return stop("you are not on this kernel's governor list");
    if (!person) {
      return stop(`${link.user} is not among the people who can sign in that this page read, which may be cut off, or may use a different address`);
    }
    if (!(access.grantable || []).includes(link.tag)) {
      return stop(`${link.tag} is not a tag this app may grant`);
    }
    if ((person.groups || []).includes(link.tag)) {
      return stop(`${link.user} already holds ${link.tag}. If the app still refuses them, they need to sign in again to get a new token`);
    }
    setPending({ subject: person.username, tag: link.tag, op: "add" });
  }, [link, access]);

  // Runs when a change is proposed and not before. It is a real read of the
  // partition, so firing one per chip on render would make opening this page a
  // census per person per tag.
  useEffect(() => {
    if (!pending || !access?.can_preview) return;
    let live = true;
    setPreviewing(true);
    setPreview(null);
    setPreviewFailure(null);
    post("/access/preview", {
      username: pending.subject,
      add: pending.op === "add" ? [pending.tag] : [],
      remove: pending.op === "remove" ? [pending.tag] : [],
    })
      .then((body) => live && setPreview(body))
      .catch((e) => live && setPreviewFailure(e))
      .finally(() => live && setPreviewing(false));
    return () => {
      live = false;
    };
  }, [pending, post, access?.can_preview]);

  async function confirmGrant() {
    setGranting(true);
    try {
      const body = await post("/access/grant", {
        username: pending.subject,
        tag: pending.tag,
        op: pending.op,
      });
      setPending(null);
      setPreview(null);
      await load();
      setOutcome({ ok: true, body });
    } catch (e) {
      setOutcome({ ok: false, error: e });
      setPending(null);
    } finally {
      setGranting(false);
    }
  }

  // Straight to the grant: app access widens no read, so there is no
  // preview to show. Same route, same two gates, same audit row as a tag.
  async function toggleApp(username, access, on) {
    setGranting(true);
    try {
      const body = await post("/access/grant", {
        username, access, op: on ? "add" : "remove",
      });
      await load();
      setOutcome({ ok: true, body: { ...body, tag: body.tag || access } });
    } catch (e) {
      setOutcome({ ok: false, error: e });
    } finally {
      setGranting(false);
    }
  }

  // Returns whether it went through, so the field that asked can close.
  async function saveLimit(body) {
    setLimitBusy(true);
    setLimitProblem(null);
    try {
      setLimits(await post("/budgets/builder", body));
      return true;
    } catch (e) {
      setLimitProblem(e?.body?.detail || e?.message || "The kernel refused that.");
      return false;
    } finally {
      setLimitBusy(false);
    }
  }

  async function saveAskLimit(body) {
    setAskBusy(true);
    setAskProblem(null);
    try {
      setAskLimits(await post("/insights/ask/limit", body));
      return true;
    } catch (e) {
      setAskProblem(e?.body?.detail || e?.message || "The kernel refused that.");
      return false;
    } finally {
      setAskBusy(false);
    }
  }

  const run = async (fn) => {
    setBusy(true);
    setProblem(null);
    try {
      await fn();
      await load();
    } catch (e) {
      setProblem(e?.body?.detail || e?.message || "The kernel refused that.");
    } finally {
      setBusy(false);
    }
  };

  const users = roster?.users || [];
  const principals = access?.principals || [];

  const topics = useMemo(
    () => Object.fromEntries((roster?.topics || []).map((t) => [t.name, t.detail])),
    [roster],
  );

  /**
   * One row per address, carrying whichever halves exist for it.
   *
   * Ordered with the people who can sign in first, because that is the list
   * somebody arrives looking for; within each half, by address, so the order
   * does not move when somebody is subscribed to something.
   */
  const people = useMemo(() => {
    const rows = new Map();
    for (const p of principals) {
      const key = keyOf(p.email || p.username);
      if (!key) continue;
      rows.set(key, { email: p.email || p.username, principal: p, user: null });
    }
    for (const u of users) {
      const key = keyOf(u.email);
      if (!key) continue;
      const found = rows.get(key);
      if (found) found.user = u;
      else rows.set(key, { email: u.email, principal: null, user: u });
    }
    return [...rows.values()].sort((a, b) => {
      if (Boolean(a.principal) !== Boolean(b.principal)) {
        return a.principal ? -1 : 1;
      }
      return a.email.localeCompare(b.email);
    });
  }, [principals, users]);

  const held = useMemo(
    () => new Set(users.map((u) => keyOf(u.email))),
    [users],
  );

  const fromPool = useMemo(
    () => principals
      .filter((p) => p.email && !held.has(keyOf(p.email)))
      .map((p) => ({ email: p.email, name: p.username,
                     source: "directory", principal: p.username })),
    [principals, held],
  );

  const fromConnectors = useMemo(
    () => candidates
      .map((p) => ({
        email: (p.fields?.emails || [])[0] || "",
        name: p.fields?.display_name || "",
        source: `connector:${p.source?.system || "unknown"}`,
        person: p.id,
      }))
      .filter((c) => c.email && !held.has(keyOf(c.email))),
    [candidates, held],
  );

  if (failure) {
    return <Refusal failure={failure} actor="This console" onRetry={load} />;
  }
  if (!roster) return <Loading label="Reading who is here" />;

  const told = users.filter((u) => u.can_be_notified).length;
  const reach = roster.reach || {};
  const open = users.find((u) => u.email === openEmail) || null;
  const grantable = access?.grantable || [];
  const mayGrant = Boolean(access?.may_grant);
  const canWriteRoster = Boolean(post);

  return (
    <Stack spacing={4} sx={{ minWidth: 0 }}>
      <Headline
        eyebrow="People here"
        figure={people.length.toLocaleString()}
        caption={people.length === 1 ? "person" : "people"}
        basis={
          "Everybody who can sign in, plus everybody on the notification "
          + "roster. The two are different lists on purpose and this is their "
          + "union — somebody with no login is here and can open nothing, and "
          + "somebody who can sign in but is on no roster is here and hears "
          + "nothing."
        }
        lede={
          users.length
            ? `If an agent hit its spending limit this minute, ${reach.spend || 0} ${(reach.spend || 0) === 1 ? "person" : "people"} would hear about it.`
            : "Nobody is on the notification roster, so nothing this kernel notices reaches anybody."
        }
      />

      {/* The rule people get backwards, next to the levers that depend on it. */}
      {access?.tags_restrict ? (
        <Alert severity="info" variant="outlined">
          <AlertTitle>Tags restrict. They do not grant</AlertTitle>
          {access.tags_restrict}
        </Alert>
      ) : null}

      {/* Two different things can be wrong and they are two different
          sentences about two different documents. Folding them into one
          "read-only" would send somebody to change the wrong one. */}
      {access && !access.can_grant ? (
        <Alert severity="warning" variant="outlined">
          <AlertTitle>Read-only here — this app may not grant</AlertTitle>
          This app does not declare <code>access.grant</code>, so it can show
          who holds what and cannot change it. The manifest is the ceiling for
          writes as well as reads.
        </Alert>
      ) : access && !access.governance?.may_govern ? (
        <Alert severity="info" variant="outlined">
          <AlertTitle>You may not change what people can see</AlertTitle>
          <Typography variant="body2">
            This app declares <code>access.grant</code> — that half passes —
            and granting additionally needs you to be a governor of this
            kernel, which is a different decision by a different document. The
            group chips below are inert for you. What people are <em>told</em>
            is a separate grant and is not affected.
          </Typography>
        </Alert>
      ) : null}

      {access?.truncated ? (
        <Alert severity="warning" variant="outlined">
          <AlertTitle>Showing the first {access.cap} who can sign in</AlertTitle>
          The pool holds more. Somebody missing here is missing from the page,
          not from the pool — do not read an absence as "no access".
        </Alert>
      ) : null}

      {!access ? (
        <Alert severity="info" variant="outlined">
          <AlertTitle>Showing the roster only</AlertTitle>
          The sign-in directory could not be read, so this page is the
          notification roster and nothing about what anybody can see. That is
          half the answer, and it is saying so rather than showing a short list
          as if it were the whole one.
        </Alert>
      ) : null}

      <BuilderLimits limits={limits} failure={limitsFailure} problem={limitProblem}
                     busy={limitBusy} onSave={saveLimit}
                     onDismiss={() => setLimitProblem(null)} />

      <InsightsAskLimits limits={askLimits} problem={askProblem} busy={askBusy}
                         onSave={saveAskLimit} onDismiss={() => setAskProblem(null)} />
      {askFailure ? (
        <Alert severity="info" variant="outlined">
          <AlertTitle>Insights question limits could not be read</AlertTitle>
          <Typography variant="body2">{askFailure.body?.detail || askFailure.message}</Typography>
        </Alert>
      ) : null}

      {outcome ? (
        <Alert
          severity={outcome.ok ? "success" : "error"}
          variant="outlined"
          onClose={() => setOutcome(null)}
        >
          <AlertTitle>
            {outcome.ok
              ? {
                  granted: "Granted",
                  revoked: "Revoked",
                  already_held: "No change — already held",
                  not_held: "No change — was not held",
                }[outcome.body.result]
              : "The change did not go through"}
          </AlertTitle>
          {outcome.ok ? (
            <Stack spacing={0.5}>
              <Typography variant="body2">
                <code>{outcome.body.tag}</code>{" "}
                {outcome.body.op === "add" ? "for" : "from"}{" "}
                {outcome.body.subject}. Recorded as{" "}
                <code>{outcome.body.action}</code> by{" "}
                <code>{outcome.body.provenance.on_behalf_of || "nobody"}</code>.
              </Typography>
              {(outcome.body.does_not_prove || []).map((line) => (
                <Typography variant="body2" color="text.secondary" key={line}>
                  {line}
                </Typography>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2">{outcome.error.message}</Typography>
          )}
        </Alert>
      ) : null}

      {problem ? (
        <Alert severity="error" variant="outlined" onClose={() => setProblem(null)}>
          {problem}
        </Alert>
      ) : null}

      <Box sx={{ minWidth: 0 }}>
        <Label>Everybody here</Label>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, maxWidth: "62ch" }}>
          One row per person. <strong>Can see</strong> is a group in the
          sign-in pool and changing it shows you what it would expose first.{" "}
          <strong>Told about</strong> is a subscription in this kernel and
          grants nothing — somebody can be told that an agent overspent
          without being able to open a single record.
        </Typography>
        <Stack spacing={1.5}>
          {people.map((person) => (
            <PersonCard
              key={person.email}
              person={person}
              groups={access?.groups || []}
              grantable={grantable}
              mayGrant={mayGrant}
              canWriteRoster={canWriteRoster}
              busy={busy || granting}
              apps={access?.app_access || []}
              onApp={toggleApp}
              limits={limits}
              limitBusy={limitBusy}
              onLimit={saveLimit}
              onGroup={setPending}
              onTopics={(user) => setOpenEmail(user.email)}
            />
          ))}
        </Stack>
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Label>Add somebody to the roster</Label>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, maxWidth: "62ch" }}>
          This adds them to the notification roster and gives them no access to
          anything. A login is a separate thing and is not created here.
        </Typography>
        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", mb: 2 }}>
          <TextField
            size="small"
            label="Email"
            value={draft.email}
            disabled={busy || !canWriteRoster}
            onChange={(e) => setDraft({ ...draft, email: e.target.value })}
            sx={{ minWidth: "18rem" }}
          />
          <TextField
            size="small"
            label="Name"
            value={draft.name}
            disabled={busy || !canWriteRoster}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            sx={{ minWidth: "14rem" }}
          />
          <Button
            variant="contained"
            disabled={busy || !canWriteRoster || !draft.email.trim()}
            onClick={() =>
              run(async () => {
                await post("/users", {
                  email: draft.email.trim(),
                  name: draft.name.trim(),
                  source: "manual",
                  topics: [],
                });
                setDraft({ email: "", name: "" });
              })
            }
          >
            Add
          </Button>
        </Stack>

        <Stack spacing={1.5}>
          <Seed
            title="People who can already sign in"
            note="This org has already trusted these addresses, so seeding from them is the cheapest honest start. Adding somebody here changes nothing about what they can see."
            candidates={fromPool}
            busy={busy || !canWriteRoster}
            onAdd={(c) => run(() => post("/users", { ...c, topics: [] }))}
          />
          <Seed
            title="People a connector knows about"
            note="A pick, never an import. Most of these are counterparties rather than operators, and a button that said 'add all' would put another company's contact on the list that receives this org's spending alerts."
            candidates={fromConnectors}
            busy={busy || !canWriteRoster}
            onAdd={(c) => run(() => post("/users", { ...c, topics: [] }))}
          />
        </Stack>
      </Box>

      <PreviewDialog
        pending={pending}
        preview={preview}
        failure={previewFailure}
        busy={previewing}
        granting={granting}
        canPreview={Boolean(access?.can_preview)}
        onCancel={() => {
          setPending(null);
          setPreview(null);
        }}
        onConfirm={confirmGrant}
      />

      <UserDrawer
        user={open}
        topics={topics}
        busy={busy}
        onClose={() => setOpenEmail(null)}
        onSave={(next) =>
          run(async () => {
            await post("/users", next);
            setOpenEmail(null);
          })
        }
        onRemove={(user) =>
          run(async () => {
            await remove(`/users/${encodeURIComponent(user.email)}`);
            setOpenEmail(null);
          })
        }
      />
    </Stack>
  );
}
